import React from "react"
import { useShortcut } from "components/CanvasEditor/Editor"
import ViewportShortcuts from "components/CanvasEditor/ViewportShortcuts"
import { getElementAABB } from "components/CanvasEditor/lib/geometry"
import { useBadgeTemplate } from "./hooks/useBadgeTemplate"
import { useBadgeSelection } from "./contexts/BadgeSelectionContext"
import { useBadgeElement } from "./contexts/BadgeElementContext"

type Direction = "up" | "down" | "left" | "right"

/** The Badge Builder's keyboard shortcuts (keybindings.ts). Element ones need a selection. */
const BadgeShortcuts = () => {
  const { currentTemplate, handleUndo, handleRedo } = useBadgeTemplate()
  const { selectedElementIds, selectMultiple, handleDeselectElement } = useBadgeSelection()
  const {
    duplicateSelectedOrById,
    deleteSelectedElements,
    bringSelectedForward,
    sendSelectedBackward,
    bringToFront,
    sendToBack,
    alignSelection,
    handleCopyElement,
    handlePasteElement,
    handleCutElement,
    hasCopiedElement,
    handleMoveElement,
  } = useBadgeElement()

  // Without a selection, the key is left to the browser
  const withSelection = (action: () => void) => () => {
    if (selectedElementIds.length === 0) return false
    action()

    return true
  }

  // Tab walks the layers in the order the layers panel lists them, from the top
  const layers = [...currentTemplate.badgeData.elements].sort((a, b) => b.zIndex - a.zIndex)
  const selectAdjacent = (step: 1 | -1) => withSelection(() => {
    const index = layers.findIndex((element) => element.id === selectedElementIds[0])
    const next = layers[(index + step + layers.length) % layers.length]
    selectMultiple([next.id])
  })
  const nudge = (direction: Direction, distance: number) => withSelection(
    () => handleMoveElement(direction, distance),
  )

  useShortcut("SELECT_ALL", () => selectMultiple(layers.map((element) => element.id)))
  useShortcut("DESELECT", withSelection(handleDeselectElement))
  useShortcut("SELECT_NEXT", selectAdjacent(1))
  useShortcut("SELECT_PREVIOUS", selectAdjacent(-1))
  useShortcut("UNDO", handleUndo)
  useShortcut("REDO", handleRedo)
  useShortcut("DUPLICATE", withSelection(() => duplicateSelectedOrById()))
  useShortcut("DELETE", withSelection(deleteSelectedElements))
  useShortcut("COPY", withSelection(handleCopyElement))
  // Pasting needs something copied, not a selection, so a cut can be pasted back
  useShortcut("PASTE", () => {
    if (!hasCopiedElement) return false
    handlePasteElement()

    return true
  })
  useShortcut("CUT", withSelection(handleCutElement))
  useShortcut("FORWARD", withSelection(() => bringSelectedForward()))
  useShortcut("BACKWARD", withSelection(() => sendSelectedBackward()))
  useShortcut("TO_FRONT", withSelection(() => bringToFront()))
  useShortcut("TO_BACK", withSelection(() => sendToBack()))
  useShortcut("ALIGN_LEFT", withSelection(() => alignSelection("left")))
  useShortcut("ALIGN_RIGHT", withSelection(() => alignSelection("right")))
  useShortcut("ALIGN_TOP", withSelection(() => alignSelection("top")))
  useShortcut("ALIGN_BOTTOM", withSelection(() => alignSelection("bottom")))
  useShortcut("CENTER_HORIZONTALLY", withSelection(() => alignSelection("centerX")))
  useShortcut("CENTER_VERTICALLY", withSelection(() => alignSelection("centerY")))
  useShortcut("NUDGE_LEFT", nudge("left", 1))
  useShortcut("NUDGE_LEFT_FINE", nudge("left", 0.1))
  useShortcut("NUDGE_RIGHT", nudge("right", 1))
  useShortcut("NUDGE_RIGHT_FINE", nudge("right", 0.1))
  useShortcut("NUDGE_UP", nudge("up", 1))
  useShortcut("NUDGE_UP_FINE", nudge("up", 0.1))
  useShortcut("NUDGE_DOWN", nudge("down", 1))
  useShortcut("NUDGE_DOWN_FINE", nudge("down", 0.1))

  // ⇧2 fits the selection's bounding box
  const getSelectionBounds = () => {
    const selected = currentTemplate.badgeData.elements
      .filter((element) => selectedElementIds.includes(element.id))
    if (selected.length === 0) return null

    const boxes = selected.map(
      (element) => getElementAABB(element, element.transforms?.rotate || 0),
    )
    const left = Math.min(...boxes.map((box) => box.x))
    const top = Math.min(...boxes.map((box) => box.y))

    return {
      x: left,
      y: top,
      width: Math.max(...boxes.map((box) => box.x + box.width)) - left,
      height: Math.max(...boxes.map((box) => box.y + box.height)) - top,
    }
  }

  return <ViewportShortcuts getSelectionBounds={getSelectionBounds} />
}

export default BadgeShortcuts
